import { yupResolver } from "@hookform/resolvers/yup";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  InputLabel,
  TextField,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { FaCaretDown } from "react-icons/fa";
import { useMutation } from "react-query";
import { useLocation } from "react-router-dom";
import * as yup from "yup";

import { useProject } from "../../../../../contexts/project.context";
import ProjectSummaryService from "../../../../../services/projectSummary.service";
import { BudgetStatus } from "../../../../../types/budget/budget";
import { NotificationFields } from "../../../../../types/notification";
import { TRANSITION_REQUIREMENTS } from "../../../../../utils/briefingStatus";
import {
  formatCurrency,
  formatProfitability,
} from "../../../../../utils/formatters";
import { AlertModal } from "../../../../AlertModal";
import CurrencyInputForm from "../../../../CurrencyInputForm";
import { Input } from "../../../../Input";
import Notification from "../../../../UI/Notification";
import SummarySection from "../../SummarySection";
import { ReactComponent as CompleteCircleIcon } from "../../../../../assets/svg/project/circle-arrow-complete.svg";
import { ReactComponent as IncompleteCircleIcon } from "../../../../../assets/svg/project/circle-arrow-incomplete.svg";
import { ReactComponent as CircleInfo } from "../../../../../assets/svg/project/circle-info.svg";
import { toast } from "react-toastify";
import ReplicateSummary from "../../ReplicateSummary";
import InputPercentage from "../../../../InputPercentage";
import InputForm from "../../../../InputForm";
import {
  formatBRLCurrency,
  formatPercentage,
  formatter,
  getSummaryTotals,
  roundTaxPercentage,
  TAX_PERCENTAGE_DECIMALS,
} from "../../../utils";
import DynamicInput from "../../../../DynamicInput";
import NumberFormat from "react-number-format";

const validationSchema = yup.object({
  totalValueOfEvents: yup.number(),
  directPaymentOfClients: yup.number(),
  observations: yup.string(),
  logisticFee: yup.number(),
  logisticMarkup: yup.number(),
  logisticComission: yup.number(),
  airFee: yup.number(),
  airMarkup: yup.number(),
  airComission: yup.number(),
  RSVP: yup.number(),
  communicationFee: yup.number(),
  loss: yup.number(),
  lossJustification: yup.string(),
  totalTaxes: yup.number(),
  totalTaxesFee: yup.number(),
  additionalTaxes: yup.number(),
  taxPercentage: yup.number(),
  totalFees: yup.number(),
  totalMarkupAndOverhead: yup.number(),
  profitability: yup.number(),
  totalRevenue: yup.number(),
  invoicedMM: yup.number(),
  logisticTotal: yup.number(),
  airTotal: yup.number(),
  comissionTotal: yup.number(),
  supplierCostTotal: yup.number(),
  totalRevenueMinusTaxes: yup.number(),
});

const SUMMARY_STATUS = BudgetStatus.AUDITED;
const MIN_TAX_PERCENTAGE = 6.15;

const AuditedAccordion = () => {
  const {
    project,
    updateSummaryContext,
    summary,
    handleSetUnsavedSummary,
    showNotification,
  } = useProject();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const projectId = searchParams.get("projectId") || "";

  const [hasOpened, setHasOpened] = useState(false);
  const [summaryData, setSummaryData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [alertModal, setAlertModal] = useState(false);
  const [tempForm, setTempForm] = useState({});
  const currentFormRef = useRef<any>({});
  const totalsRef = useRef<any>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [hasFilled, setHasFilled] = useState(false);
  const [showReplicate, setShowReplicate] = useState(false);
  const [lastFocus, setLastFocus] = useState("");
  const [formatValue, setFormattedValue] = useState("R$ 0,00");
  const [percent, setPercent] = useState<any>(0);

  const checkPendingValues = () => {
    const projectStatus = project?.status?.status || "";

    const closerSummaryStatus = TRANSITION_REQUIREMENTS[projectStatus];

    const pendingSummary = summary
      ? summary?.[closerSummaryStatus?.summary]
      : false;

    const summaryRequiredFields = [
      "totalValueOfEvents",
      "directPaymentOfClients",
      "logisticFee",
      "logisticMarkup",
      "logisticComission",
      "airFee",
      "airMarkup",
      "airComission",
      "RSVP",
      "communicationFee",
      "taxPercentage",
    ];

    const summaryValues = ["PRE_CLOSING", "AUDITED", "APPROVED"];

    return (
      !summaryValues.some((item) => item === projectStatus) &&
      closerSummaryStatus?.summary === SUMMARY_STATUS &&
      summaryRequiredFields.some(
        (item) =>
          pendingSummary?.[item] === undefined ||
          pendingSummary?.[item] === null
      )
    );
  };

  const canEdit = project?.status?.status === "AUDITED" || checkPendingValues();

  const methods = useForm({
    defaultValues: {
      totalValueOfEvents: 0,
      directPaymentOfClients: 0,
      observations: "",
      logisticFee: 0,
      logisticMarkup: 0,
      logisticComission: 0,
      airFee: 0,
      airMarkup: 0,
      airComission: 0,
      RSVP: 0,
      communicationFee: 0,
      loss: 0,
      lossJustification: "",
      totalTaxes: 0,
      totalTaxesFee: 0,
      additionalTaxes: 0,
      taxPercentage: MIN_TAX_PERCENTAGE,
      totalFees: 0,
      totalMarkupAndOverhead: 0,
      profitability: 0,
      totalRevenue: 0,
      invoicedMM: 0,
      logisticTotal: 0,
      airTotal: 0,
      comissionTotal: 0,
      supplierCostTotal: 0,
      totalRevenueMinusTaxes: 0,
    },
    resolver: yupResolver(validationSchema),
    mode: "all",
  });

  const {
    setValue,
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    control,
  } = methods;

  const formWatch: yup.InferType<typeof validationSchema> = useWatch({
    control,
  });

  useEffect(() => {
    handleSetUnsavedSummary({ active: false, saveFn: null });
  }, []);

  useEffect(() => {
    const watch: any = formWatch;
    const hasChanges = Object.entries(currentFormRef.current).some(
      ([key, value]: any) => {
        if (key === "additionalTaxes") {
          return watch[key]?.toFixed(2) !== value?.toFixed(2);
        }

        return watch[key] != value;
      }
    );

    setHasChanges(hasChanges);
    handleSetUnsavedSummary({
      active: hasChanges,
      saveFn: hasChanges ? handleSubmit(onAlertSubmit) : null,
    });
  }, [formWatch]);

  useEffect(() => {
    if (formWatch) {
      const {
        logisticTotal,
        airTotal,
        totalFees,
        totalMarkupAndOverhead,
        comissionTotal,
        totalRevenue,
        totalTaxesFee,
        totalRevenueMinusTaxes,
        profitability,
        supplierCostTotal,
        invoicedMM,
        totalTaxes,
      } = getSummaryTotals(formWatch);

      setValue("logisticTotal", logisticTotal);
      setValue("airTotal", airTotal);
      setValue("totalFees", totalFees);
      setValue("totalMarkupAndOverhead", totalMarkupAndOverhead);
      setValue("comissionTotal", comissionTotal);
      setValue("totalRevenue", totalRevenue);
      setValue("totalTaxesFee", totalTaxesFee);
      setValue("totalRevenueMinusTaxes", totalRevenueMinusTaxes);
      setValue("profitability", isFinite(profitability) ? profitability : 0);
      setValue("supplierCostTotal", supplierCostTotal);
      setValue("invoicedMM", invoicedMM);
      setValue("totalTaxes", totalTaxes);
    }
  }, [
    formWatch.logisticFee,
    formWatch.logisticComission,
    formWatch.logisticMarkup,
    formWatch.airFee,
    formWatch.airComission,
    formWatch.airMarkup,
    formWatch.RSVP,
    formWatch.communicationFee,
    formWatch.additionalTaxes,
    formWatch.taxPercentage,
    formWatch.totalValueOfEvents,
    formWatch.directPaymentOfClients,
    formWatch.loss,
  ]);

  useEffect(() => {
    if (formWatch) {
      const { additionalTaxes } = getSummaryTotals(formWatch);

      if (lastFocus === "") {
        const formattedAdditionalTaxes = formatBRLCurrency(additionalTaxes);
        setFormattedValue(formattedAdditionalTaxes);
        setValue("additionalTaxes", formatNumber(additionalTaxes));
      }
    }
  }, [
    formWatch.logisticFee,
    formWatch.logisticComission,
    formWatch.logisticMarkup,
    formWatch.airFee,
    formWatch.airComission,
    formWatch.airMarkup,
    formWatch.RSVP,
    formWatch.communicationFee,
    formWatch.totalValueOfEvents,
    formWatch.directPaymentOfClients,
  ]);

  function formatNumber(value: number) {
    return Number(value.toFixed(3));
  }

  useEffect(() => {
    if (lastFocus === "value") {
      const totalFees =
        (formWatch.airFee || 0) +
        (formWatch.logisticFee || 0) +
        (formWatch.RSVP || 0) +
        (formWatch.communicationFee || 0);

      const totalMarkupAndOverhead =
        (formWatch.logisticMarkup || 0) + (formWatch.airMarkup || 0);

      const comissionTotal =
        (formWatch.logisticComission || 0) + (formWatch.airComission || 0);

      const newTaxPercentage =
        (((formWatch.additionalTaxes || 0) + (formWatch.totalTaxes || 0)) /
          (comissionTotal + totalMarkupAndOverhead + totalFees)) *
        100;
      setPercent(formatNumber(newTaxPercentage));
      setValue("taxPercentage", roundTaxPercentage(newTaxPercentage));
    }
  }, [formWatch.additionalTaxes]);

  useEffect(() => {
    if (lastFocus === "percentage") {
      const totalFees =
        (formWatch.airFee || 0) +
        (formWatch.logisticFee || 0) +
        (formWatch.RSVP || 0) +
        (formWatch.communicationFee || 0);

      const totalMarkupAndOverhead =
        (formWatch.logisticMarkup || 0) + (formWatch.airMarkup || 0);

      const comissionTotal =
        (formWatch.logisticComission || 0) + (formWatch.airComission || 0);

      const totalTaxes =
        (comissionTotal + totalMarkupAndOverhead + totalFees) *
        (MIN_TAX_PERCENTAGE / 100);

      const invisibleTaxes =
        (comissionTotal + totalMarkupAndOverhead + totalFees) *
        ((formWatch.taxPercentage || 0) / 100);

      const newAdditionalTaxes = invisibleTaxes - totalTaxes;

      const formattedAdditionalTaxes = formatBRLCurrency(newAdditionalTaxes);

      setFormattedValue(formattedAdditionalTaxes);
      setValue("additionalTaxes", formatNumber(newAdditionalTaxes));
    }
  }, [formWatch.taxPercentage]);

  useEffect(() => {
    const hasFilled = summary["AUDITED"]?.hasOwnProperty("totalValueOfEvents");
    setHasFilled(hasFilled);
  }, [summary]);

  useEffect(() => {
    setShowReplicate(canEdit);
  }, [canEdit]);

  const getSummary = async (forceRefresh = false) => {
    reset();
    setLoading(true);

    const cached = summary?.AUDITED;
    if (!forceRefresh && cached?._id) {
      setSummaryData(cached);
      setLoading(false);
      return;
    }

    const data = await ProjectSummaryService.get({
      project: projectId,
      status: SUMMARY_STATUS,
    });
    if (!data?.length) {
      await Promise.all([
        ProjectSummaryService.create({
          project: projectId,
          status: BudgetStatus.ON_APPROVAL,
        }),
        ProjectSummaryService.create({
          project: projectId,
          status: BudgetStatus.APPROVED,
        }),
        ProjectSummaryService.create({
          project: projectId,
          status: BudgetStatus.CLOSURE,
        }),
        ProjectSummaryService.create({
          project: projectId,
          status: BudgetStatus.AUDITED,
        }),
      ]);
      getSummary(true);
    } else {
      setSummaryData(data?.[0]);
      updateSummaryContext("AUDITED", data?.[0]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasOpened) getSummary();
  }, [hasOpened]);

  useEffect(() => {
    setValue("totalValueOfEvents", summaryData?.totalValueOfEvents || 0);
    currentFormRef.current["totalValueOfEvents"] =
      summaryData?.totalValueOfEvents || 0;
    setValue(
      "directPaymentOfClients",
      summaryData?.directPaymentOfClients || 0
    );
    currentFormRef.current["directPaymentOfClients"] =
      summaryData?.directPaymentOfClients || 0;
    setValue("logisticFee", summaryData?.logisticFee || 0);
    currentFormRef.current["logisticFee"] = summaryData?.logisticFee || 0;
    setValue("logisticMarkup", summaryData?.logisticMarkup || 0);
    currentFormRef.current["logisticMarkup"] = summaryData?.logisticMarkup || 0;
    setValue("logisticComission", summaryData?.logisticComission || 0);
    currentFormRef.current["logisticComission"] =
      summaryData?.logisticComission || 0;
    setValue("airFee", summaryData?.airFee || 0);
    currentFormRef.current["airFee"] = summaryData?.airFee || 0;
    setValue("airMarkup", summaryData?.airMarkup || 0);
    currentFormRef.current["airMarkup"] = summaryData?.airMarkup || 0;
    setValue("airComission", summaryData?.airComission || 0);
    currentFormRef.current["airComission"] = summaryData?.airComission || 0;
    setValue("RSVP", summaryData?.RSVP || 0);
    currentFormRef.current["RSVP"] = summaryData?.RSVP || 0;
    setValue("communicationFee", summaryData?.communicationFee || 0);
    currentFormRef.current["communicationFee"] =
      summaryData?.communicationFee || 0;
    setValue("loss", summaryData?.loss || 0);
    currentFormRef.current["loss"] = summaryData?.loss || 0;
    setValue("lossJustification", summaryData?.lossJustification || "");
    currentFormRef.current["lossJustification"] = summaryData?.lossJustification || "";
    setValue("observations", summaryData?.observations || "");
    currentFormRef.current["observations"] = summaryData?.observations || "";
    setValue("taxPercentage", summaryData?.taxPercentage || MIN_TAX_PERCENTAGE);
    setPercent(formatNumber(summaryData?.taxPercentage || MIN_TAX_PERCENTAGE));
    currentFormRef.current["taxPercentage"] =
      summaryData?.taxPercentage || MIN_TAX_PERCENTAGE;

    setValue("totalTaxes", summaryData?.totalTaxes || 0);
    setValue("totalTaxesFee", summaryData?.totalTaxesFee || 0);

    setValue(
      "additionalTaxes",
      summaryData?.additionalTaxes < 0.001 ? 0 : summaryData?.additionalTaxes
    );
    currentFormRef.current["additionalTaxes"] =
      summaryData?.additionalTaxes < 0.001 ? 0 : summaryData?.additionalTaxes;

    if (summaryData?.additionalTaxes) {
      const formattedAdditionalTaxes = formatBRLCurrency(
        summaryData?.additionalTaxes
      );
      setFormattedValue(formattedAdditionalTaxes);
    }

    setValue("totalFees", summaryData?.totalFees || 0);
    setValue(
      "totalMarkupAndOverhead",
      summaryData?.totalMarkupAndOverhead || 0
    );
    setValue("profitability", summaryData?.profitability || 0);
    setValue("totalRevenue", summaryData?.totalRevenue || 0);
    setValue("invoicedMM", summaryData?.invoicedMM || 0);
    setValue("logisticTotal", summaryData?.logisticTotal || 0);
    setValue("airTotal", summaryData?.airTotal || 0);
    setValue("comissionTotal", summaryData?.comissionTotal || 0);
    setValue("supplierCostTotal", summaryData?.supplierCostTotal || 0);
    setValue(
      "totalRevenueMinusTaxes",
      summaryData?.totalRevenueMinusTaxes || 0
    );

    totalsRef.current["supplierCostTotal"] =
      summaryData?.supplierCostTotal || 0;
    totalsRef.current["totalRevenue"] = summaryData?.totalRevenue || 0;
    totalsRef.current["invoicedMM"] = summaryData?.invoicedMM || 0;
    totalsRef.current["totalRevenueMinusTaxes"] =
      summaryData?.totalRevenueMinusTaxes || 0;
  }, [summaryData]);

  const { mutateAsync: updateSummary, isLoading: isLoadingUpdate } = useMutation(
    (state: any) => ProjectSummaryService.update(state.id, state),
    {
      onSuccess: () => {
        getSummary(true);
      },
      onError: (error) => {
        toast.error("Não foi possível salvar o status", {
          pauseOnHover: false,
          autoClose: 2000,
        });
      },
    }
  );

  function checkEmptyValues(obj: any) {
    const blackList = [
      "comissionTotal",
      "invoicedMM",
      "profitability",
      "supplierCostTotal",
      "totalFees",
      "totalMarkupAndOverhead",
      "totalRevenue",
      "logisticTotal",
      "airTotal",
      "totalTaxes",
      "totalTaxesFee",
      "additionalTaxes",
      "totalRevenueMinusTaxes",
    ];

    for (const key in obj) {
      if (
        obj.hasOwnProperty(key) &&
        !blackList.includes(key) &&
        obj[key] === 0
      ) {
        return true;
      }
    }
    return false;
  }

  function hasFieldChanged(fieldName: string, value: any) {
    const originalValue = currentFormRef.current?.[fieldName];
    if (typeof value === "number" || typeof originalValue === "number") {
      const decimals = fieldName === "taxPercentage" ? TAX_PERCENTAGE_DECIMALS : 3;
      return Number(value || 0).toFixed(decimals) !== Number(originalValue || 0).toFixed(decimals);
    }
    return value !== originalValue;
  }

  function buildChangedPayload(form: any) {
    const changedEntries = Object.entries(form).filter(([fieldName, value]) =>
      hasFieldChanged(fieldName, value)
    );
    return Object.fromEntries(changedEntries);
  }

  async function saveSummary(form: any) {
    const changedPayload = buildChangedPayload(form);
    if (Object.keys(changedPayload).length === 0) {
      return true;
    }

    try {
      await updateSummary({
        id: summaryData?._id,
        project: projectId,
        status: SUMMARY_STATUS,
        ...changedPayload,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  const onSubmit = async (form: any) => {
    if (form?.taxPercentage < MIN_TAX_PERCENTAGE) {
      showNotification(
        "O percentual de imposto deve ser no mínimo 6,15 %",
        "error"
      );
      return false;
    }

    if (checkEmptyValues(form)) {
      setAlertModal(true);
      setTempForm(form);
    } else {
      return await saveSummary(form);
    }
    return true;
  };

  const onAlertSubmit = async (form: any) => {
    if (form?.taxPercentage < MIN_TAX_PERCENTAGE) {
      showNotification(
        "O percentual de imposto deve ser no mínimo 6,15 %",
        "error"
      );
      return false;
    }

    return await saveSummary(form);
  };

  const makeDecision = async (flag: boolean) => {
    if (flag) {
      await saveSummary(tempForm);
    }
    setAlertModal(false);
    setTempForm({});
  };

  const replicateSummary = async () => {
    const data = await ProjectSummaryService.get({
      project: projectId,
      status: BudgetStatus.CLOSURE,
    });
    if (data?.length) {
      const summaryToReplicate = data?.[0];
      updateSummary({
        totalValueOfEvents: summaryToReplicate?.totalValueOfEvents,
        directPaymentOfClients: summaryToReplicate?.directPaymentOfClients,
        observations: summaryToReplicate?.observations,
        logisticFee: summaryToReplicate?.logisticFee,
        logisticMarkup: summaryToReplicate?.logisticMarkup,
        logisticComission: summaryToReplicate?.logisticComission,
        airFee: summaryToReplicate?.airFee,
        airMarkup: summaryToReplicate?.airMarkup,
        airComission: summaryToReplicate?.airComission,
        RSVP: summaryToReplicate?.RSVP,
        communicationFee: summaryToReplicate?.communicationFee,
        taxPercentage: summaryToReplicate?.taxPercentage,
        id: summaryData?._id,
        project: projectId,
        status: SUMMARY_STATUS,
      });
    }
  };

  function formatNumberToBRL(event: React.ChangeEvent<HTMLInputElement>): void {
    let userInput: string = event.target.value.replace(/[^0-9]/g, "");

    if (userInput === "") {
      setFormattedValue("R$ 0,00");
      setValue("additionalTaxes", 0);
    } else {
      let userInputAsNumber: number = parseInt(userInput, 10) / 100;

      let formattedNumber: string = `R$ ${userInputAsNumber
        .toFixed(2)
        .replace(".", ",")
        .replace(/(\d)(?=(\d{3})+\,)/g, "$1.")}`;

      setFormattedValue(formattedNumber);
      setValue("additionalTaxes", Number(userInputAsNumber.toFixed(3)));
    }
  }

  return (
    <>
      <AlertModal
        handleClose={() => setAlertModal(false)}
        isOpen={alertModal}
        makeDecision={makeDecision}
        dontShowAgain={false}
        mainText="Existem valores preenchidos como zero no formulário. Deseja prosseguir ?"
      />
      <Accordion
        TransitionProps={{ unmountOnExit: true }}
        style={{ borderRadius: 4 }}
      >
        <AccordionSummary
          aria-controls="panel1a-content"
          expandIcon={<FaCaretDown color="white" />}
          id="panel1a-header"
          style={{
            maxHeight: "40px",
            display: "flex",
            alignItems: "center",
            backgroundColor: "#738696",
            borderRadius: 4,
          }}
          onClick={() => setHasOpened(true)}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              }}
            >
              {hasFilled ? <CompleteCircleIcon /> : <IncompleteCircleIcon />}
              <span style={{ color: "white" }}>Auditado</span>
            </div>
            {isLoadingUpdate ? (
              <CircularProgress
                style={{ color: "white", marginRight: 20 }}
                size={24}
              />
            ) : (
              canEdit && (
                <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
                  {hasChanges && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        padding: "8px 16px",
                        borderRadius: 4,
                        gap: 8,
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                      }}
                    >
                      <CircleInfo />
                      <span style={{ color: "#FFFFFF" }}>
                        Alterações não salvas
                      </span>
                    </div>
                  )}
                  {hasChanges && (
                    <div
                      style={{
                        color: "#1976D2",
                        marginRight: 16,
                        cursor: "pointer",
                        padding: "8px 16px",
                        backgroundColor: "#FFFFFF",
                        borderRadius: 4,
                        fontWeight: 600,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmit(onSubmit)();
                      }}
                    >
                      Salvar
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        </AccordionSummary>

        <AccordionDetails>
          {loading ? (
            <Box
              display={"flex"}
              flex={1}
              alignItems={"center"}
              justifyContent={"center"}
            >
              <CircularProgress style={{ margin: 16 }} />
            </Box>
          ) : (
            <FormProvider {...methods}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  gap: 2,
                }}
              >
                {showReplicate && (
                  <ReplicateSummary
                    fromStatus={BudgetStatus.CLOSURE}
                    setShowReplicate={setShowReplicate}
                    onReplicate={replicateSummary}
                  />
                )}
                <SummarySection title="Previsão de Custo e Financeiro">
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <CurrencyInputForm
                      label="Valor Total do Evento"
                      name="totalValueOfEvents"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Pagamento Direto do Cliente"
                      name="directPaymentOfClients"
                      disabled={!canEdit}
                    />
                  </Box>
                </SummarySection>

                <span style={{ fontWeight: "bold" }}>Total de Receitas</span>

                <SummarySection title="Totais de receita Logistica / Produção">
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <CurrencyInputForm
                      label="Fee"
                      name="logisticFee"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Markup / Over"
                      name="logisticMarkup"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Comissão"
                      name="logisticComission"
                      disabled={!canEdit}
                    />
                    <DynamicInput
                      label="Total Receitas"
                      name="logisticTotal"
                      value={formatter(watch("logisticTotal"))}
                      disabled
                    />
                  </Box>
                </SummarySection>

                <SummarySection title="Totais de receita Aéreo">
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <CurrencyInputForm
                      label="Fee"
                      name="airFee"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Markup / Over"
                      name="airMarkup"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Comissão/Incentivo"
                      name="airComission"
                      disabled={!canEdit}
                    />
                    <DynamicInput
                      label="Total Receitas"
                      name="airTotal"
                      value={formatter(watch("airTotal"))}
                      disabled
                    />
                  </Box>
                </SummarySection>

                <SummarySection title="Outras receitas - Prejuízo">
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <CurrencyInputForm
                      label="RSVP"
                      name="RSVP"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Comunicação"
                      name="communicationFee"
                      disabled={!canEdit}
                    />
                    <CurrencyInputForm
                      label="Prejuízo"
                      name="loss"
                      disabled={!canEdit}
                    />
                  </Box>
                  <Box mt={2}>
                    <InputForm
                      label="Justificar prejuízo"
                      disabled={!canEdit}
                      name="lossJustification"
                      placeholder="Descreva a justificativa do prejuízo"
                    />
                  </Box>
                </SummarySection>

                <SummarySection title="Impostos">
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <DynamicInput
                      label="Total de impostos ISS/PIS/COFINS"
                      name="totalTaxes"
                      value={formatter(watch("totalTaxes"))}
                      disabled
                    />
                    <Box width={"100%"}>
                      <InputLabel sx={{ fontSize: 13 }}>Valor</InputLabel>
                      <TextField
                        onFocus={() => setLastFocus("value")}
                        onBlur={() => setLastFocus("")}
                        value={formatValue}
                        onChange={formatNumberToBRL}
                        disabled={!canEdit}
                      />
                    </Box>

                    <Box width={"100%"}>
                      <InputLabel sx={{ fontSize: 13 }}>
                        Percentual de imposto (%)
                      </InputLabel>

                      <NumberFormat
                        disabled={!canEdit}
                        onFocus={() => setLastFocus("percentage")}
                        onBlur={() => setLastFocus("")}
                        value={percent}
                        suffix=" %"
                        onChange={(e: any) => {
                          if (e.target.value === "") {
                            setPercent(null);
                            setValue("taxPercentage", 0);
                          } else {
                            const val = Number(
                              e.target.value.replace("%", "").replace(",", ".")
                            );
                            setPercent(val);
                            setValue("taxPercentage", val);
                          }
                        }}
                        decimalSeparator=","
                        customInput={TextField}
                        color={"info"}
                        size="small"
                      />

                      <span
                        style={{
                          fontSize: 14,
                          color: "rgba(1,1,1,0.5)",
                          marginTop: 4,
                        }}
                      >
                        Valor mínimo 6,15%
                      </span>
                    </Box>
                  </Box>
                </SummarySection>

                <SummarySection title="Resumo Financeiro - Totais">
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <DynamicInput
                      label="Valor Total do Evento"
                      name="totalValueOfEvents"
                      value={formatter(watch("totalValueOfEvents"))}
                      disabled
                    />
                    <DynamicInput
                      label="Pag. Direto"
                      name="directPaymentOfClients"
                      value={formatter(watch("directPaymentOfClients"))}
                      disabled
                    />

                    <DynamicInput
                      label="Custo Fornecedor"
                      name="supplierCostTotal"
                      value={formatter(watch("supplierCostTotal"))}
                      disabled
                    />

                    <DynamicInput
                      label="Faturado MM"
                      name="invoicedMM"
                      value={formatter(watch("invoicedMM"))}
                      disabled
                    />
                  </Box>
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <DynamicInput
                      label="Impostos"
                      name="totalTaxesFee"
                      value={formatter(watch("totalTaxesFee"))}
                      disabled
                    />
                    <DynamicInput
                      label="Fee"
                      name="totalFees"
                      value={formatter(watch("totalFees"))}
                      disabled
                    />

                    <DynamicInput
                      label="Markup/Over"
                      name="totalMarkupAndOverhead"
                      value={formatter(watch("totalMarkupAndOverhead"))}
                      disabled
                    />
                    <DynamicInput
                      label="Comissão"
                      name="comissionTotal"
                      value={formatter(watch("comissionTotal"))}
                      disabled
                    />
                  </Box>
                  <Box display={"flex"} margin={"4px 0px"} gap={2}>
                    <DynamicInput
                      label="Total de receitas"
                      name="totalRevenue"
                      value={formatter(watch("totalRevenue"))}
                      disabled
                    />
                     {/* estou alterando aqui */}
                    <DynamicInput
                      label="Prejuízo"
                      name="loss"
                      value={formatter(watch("loss"))}
                      disabled
                    />

                    <DynamicInput
                      label="Total receita líquida - Impostos - Prejuízo"
                      name="totalRevenueMinusTaxes"
                      value={formatter(watch("totalRevenueMinusTaxes"))}
                      disabled
                    />
                    <DynamicInput
                      label="Rentabilidade"
                      name="profitability"
                      value={formatPercentage(watch("profitability"))}
                      disabled
                    />
                  </Box>
                </SummarySection>

                <InputForm
                  label="Observações"
                  disabled={!canEdit}
                  name="observations"
                  placeholder="Exemplo: quem aprova o orçamento"
                />

                {hasChanges && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-end",
                    }}
                  >
                    <div
                      style={{
                        color: "#FFFFFF",
                        cursor: "pointer",
                        padding: 16,
                        backgroundColor: "#1976D2",
                        borderRadius: 4,
                        fontWeight: 600,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSubmit(onSubmit)();
                      }}
                    >
                      Salvar
                    </div>
                  </Box>
                )}
              </Box>
            </FormProvider>
          )}
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default AuditedAccordion;
